import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import certificationApi, { isScopedProfileSustainabilityFormId } from '../apis/certification.api';
import { AdminProfileCertificationDto, ProfileCertificationDto } from '../../models/certification';

/** Normalized cache key for profile certification lists (optionally scoped to one sustainability form). */
export const profileCertificationsCacheKey = (
	profileId: number,
	profileSustainabilityFormId?: number | null
): string =>
	isScopedProfileSustainabilityFormId(profileSustainabilityFormId)
		? `${profileId}:${profileSustainabilityFormId}`
		: `${profileId}:all`;

const initialState: {
	profileCertificationsByKey: Record<string, ProfileCertificationDto[] | null>;
	adminProfileCertifications: AdminProfileCertificationDto[];
} = {
	profileCertificationsByKey: {},
	adminProfileCertifications: []
};

const updateProfileCertificationsForKey = (
	state: { profileCertificationsByKey: Record<string, ProfileCertificationDto[] | null> },
	cacheKey: string,
	profileCertifications: ProfileCertificationDto[] | null
) => {
	state.profileCertificationsByKey[cacheKey] = profileCertifications;
};

const profileCertificationsSlice = createSlice({
	name: 'profileCertifications',
	initialState,
	reducers: {
		clearProfileCertifications(state, action: PayloadAction<number | undefined>) {
			const profileId = action.payload;
			if (profileId === undefined) {
				state.profileCertificationsByKey = {};
				return;
			}
			const prefix = `${profileId}:`;
			for (const key of Object.keys(state.profileCertificationsByKey)) {
				if (key.startsWith(prefix)) {
					delete state.profileCertificationsByKey[key];
				}
			}
		},
		setProfileCertifications(
			state,
			action: {
				payload: {
					profileId: number;
					profileSustainabilityFormId?: number;
					certifications: ProfileCertificationDto[];
				};
			}
		) {
			const { profileId, profileSustainabilityFormId, certifications } = action.payload;
			const key = profileCertificationsCacheKey(profileId, profileSustainabilityFormId);
			updateProfileCertificationsForKey(state, key, certifications);
		}
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			certificationApi.endpoints.getProfileCertifications.matchFulfilled,
			(state, action) => {
				const profileId = action.meta.arg?.originalArgs?.profileId ?? 0;
				const formId = action.meta.arg?.originalArgs?.profileSustainabilityFormId;
				const key = profileCertificationsCacheKey(profileId, formId);
				updateProfileCertificationsForKey(state, key, action.payload);
			}
		);

		builder.addMatcher(
			certificationApi.endpoints.getAllProfileCertificationsPage.matchFulfilled,
			(state, action) => {
				const next = action.payload ?? [];
				const index = action.meta.arg?.originalArgs?.index ?? 0;

				// Replace on first page, append (de-duped) on subsequent pages.
				if (index === 0) {
					state.adminProfileCertifications = next;
					return;
				}

				const existingKeys = new Set(
					state.adminProfileCertifications.map((x) => `${x.profileId}-${x.certificationId}`)
				);
				for (const row of next) {
					const key = `${row.profileId}-${row.certificationId}`;
					if (!existingKeys.has(key)) {
						state.adminProfileCertifications.push(row);
						existingKeys.add(key);
					}
				}
			}
		);

		builder.addMatcher(
			certificationApi.endpoints.setCertificationStatus.matchFulfilled,
			(state, action) => {
				const updated = action.payload;
				const { profileId, profileCertificationId } = action.meta.arg?.originalArgs ?? {};

				// Update every cached list for this profile (all / per-form).
				if (profileId !== null && profileId !== undefined) {
					const prefix = `${profileId}:`;
					for (const key of Object.keys(state.profileCertificationsByKey)) {
						if (!key.startsWith(prefix)) continue;
						const list = state.profileCertificationsByKey[key];
						if (!list) continue;
						const idx = list.findIndex((x) => x.certificationId === updated.certificationId);
						if (idx >= 0) list[idx] = updated;
					}
				}

				// Update admin list (needs profileId to match row)
				if (profileId !== null && profileId !== undefined) {
					const adminIdx = state.adminProfileCertifications.findIndex(
						(x) =>
							x.profileId === profileId &&
							(profileCertificationId !== null && profileCertificationId !== undefined
								? x.profileCertificationId === profileCertificationId
								: x.certificationId === updated.certificationId)
					);
					if (adminIdx >= 0) {
						state.adminProfileCertifications[adminIdx] = {
							...state.adminProfileCertifications[adminIdx],
							...updated,
							profileId
						};
					}
				}
			}
		);
	}
});

export const { clearProfileCertifications, setProfileCertifications } =
	profileCertificationsSlice.actions;

export const selectProfileCertificationsByProfileAndForm = (
	state: {
		profileCertifications: {
			profileCertificationsByKey: Record<string, ProfileCertificationDto[] | null>;
			adminProfileCertifications: AdminProfileCertificationDto[];
		};
	},
	profileId: number,
	profileSustainabilityFormId?: number
): ProfileCertificationDto[] | null =>
	state.profileCertifications?.profileCertificationsByKey?.[
		profileCertificationsCacheKey(profileId, profileSustainabilityFormId)
	] ?? null;

/** Cached list when fetching without a form filter (`profileSustainabilityFormId` omitted). */
export const selectProfileCertificationsByProfileId = (
	state: {
		profileCertifications: {
			profileCertificationsByKey: Record<string, ProfileCertificationDto[] | null>;
			adminProfileCertifications: AdminProfileCertificationDto[];
		};
	},
	profileId: number
): ProfileCertificationDto[] | null =>
	selectProfileCertificationsByProfileAndForm(state, profileId, undefined);

export const selectAdminProfileCertifications = (state: {
	profileCertifications: { adminProfileCertifications: AdminProfileCertificationDto[] };
}): AdminProfileCertificationDto[] => state.profileCertifications?.adminProfileCertifications ?? [];

export const selectAdminProfileCertificationByKey = (
	state: { profileCertifications: { adminProfileCertifications: AdminProfileCertificationDto[] } },
	profileCertId: number
): AdminProfileCertificationDto | undefined =>
	state.profileCertifications?.adminProfileCertifications?.find(
		(x) => x.profileCertificationId === profileCertId
	);

export default profileCertificationsSlice.reducer;
